import PropTypes from 'prop-types';
import { Box, Typography, styled } from '@mui/material';
import { ArrowDown2 } from 'iconsax-react';
import { useState, useRef, useEffect } from 'react';

// Custom dropdown input
const DropdownInput = styled('div')(({ theme, disabled, error, isOpen }) => ({
  width: '100%',
  color: theme.palette.text.primary,
  fontSize: `${theme.spacing(4)} !important`,
  fontWeight: 400,
  fontFamily: 'Poppins, sans-serif !important',
  padding: theme.spacing(4, 11, 4, 6),
  cursor: disabled ? 'not-allowed' : 'pointer',
  backgroundColor: 'transparent',
  border: `1px solid ${
    error
      ? theme.palette.error.main
      : isOpen
        ? theme.palette.secondary.main
        : theme.palette.mode === 'dark'
          ? '#787878'
          : '#C6C6C6'
  }`,
  borderRadius: theme.spacing(2),
  outline: 'none',
  userSelect: 'none',

  [theme.breakpoints.down('xl')]: {
    fontSize: `${theme.spacing(3.75)} !important`,
    padding: theme.spacing(3.5, 9, 3.5, 5.5),
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: `${theme.spacing(3.5)} !important`,
    padding: theme.spacing(3, 9, 3, 5),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: `${theme.spacing(3.25)} !important`,
    padding: theme.spacing(2, 8, 2, 4),
  },

  '&:hover:not(.disabled)': {
    borderColor: theme.palette.secondary.main,
  },

  '&.disabled': {
    cursor: 'not-allowed',
    borderColor: 'rgb(208, 208, 208)',
    opacity: 0.6,
  },
}));

// Dropdown menu container
const DropdownMenu = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: theme.spacing(1),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.surface_22
      : theme.palette.background.paper,
  border: `1px solid ${theme.palette.mode === 'dark' ? '#787878' : '#C6C6C6'}`,
  borderRadius: theme.spacing(2),
  zIndex: 1000,
  maxHeight: theme.spacing(60),
  overflowY: 'auto',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0px 4px 20px rgba(0, 0, 0, 0.5)'
      : '0px 4px 20px rgba(0, 0, 0, 0.1)',
}));

// Dropdown option
const DropdownOption = styled('div')(({ theme, isPlaceholder }) => ({
  fontSize: `${theme.spacing(4)} !important`,
  padding: theme.spacing(4, 6),
  fontWeight: 400,
  fontFamily: 'Poppins, sans-serif !important',
  backgroundColor: 'transparent',
  color: isPlaceholder
    ? theme.palette.text.disabled
    : theme.palette.text.primary,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',

  [theme.breakpoints.down('xl')]: {
    fontSize: `${theme.spacing(3.5)} !important`,
    padding: theme.spacing(3, 5.5),
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: `${theme.spacing(3.5)} !important`,
    padding: theme.spacing(2.5, 5),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: `${theme.spacing(3.25)} !important`,
    padding: theme.spacing(2, 4),
  },

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.secondary.neutral_30
        : theme.palette.secondary.neutral_95,
  },

  '&:not(:last-child)': {
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.secondary.neutral_30 : '#E5E5E5'}`,
  },
}));

const ErrorTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.spacing(3),
  fontWeight: 400,
  color: theme.palette.error.main,
  marginTop: theme.spacing(2),
}));

const ArrowDownCustom = styled(ArrowDown2)(({ theme, disabled, isOpen }) => ({
  position: 'absolute',
  top: '50%',
  right: theme.direction === 'ltr' ? theme.spacing(6) : 'auto',
  left: theme.direction === 'rtl' ? 20 : 'auto',
  fontSize: theme.spacing(5),
  transform: `translateY(-50%) ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}`,
  transition: 'transform 0.2s ease',
  [theme.breakpoints.down('xl')]: {
    width: theme.spacing(5),
    top: '50%',
  },
  [theme.breakpoints.down('sm')]: {
    width: theme.spacing(4),
    right: theme.direction === 'ltr' ? theme.spacing(4) : 'auto',
    left: theme.direction === 'rtl' ? 12 : 'auto',
  },
  cursor: disabled ? 'not-allowed' : 'pointer',
  color: disabled
    ? theme.palette.mode === 'dark'
      ? theme.palette.primary.primary_50
      : theme.palette.secondary.neutral_80
    : theme.palette.mode === 'dark'
      ? theme.palette.secondary.neutral_60
      : theme.palette.secondary.neutral_40,
  pointerEvents: 'none',
  zIndex: 1,
}));

const TextLable = styled('label')(({ theme, disabled }) => ({
  marginBottom: theme.spacing(3),
  color: disabled
    ? theme.palette.mode === 'dark'
      ? theme.palette.primary.primary_50
      : theme.palette.secondary.neutral_80
    : theme.palette.primary.main,
  display: 'block',
}));

const BaseSelectBox = (props) => {
  const {
    list = [],
    values,
    placeholder = '',
    sx = {},
    selected = () => {},
    message = '',
    error = false,
    lableText = '',
    readOnly = false,
    disabled = false,
    handleOnChange,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled && !readOnly) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (option) => {
    if (!disabled && !readOnly) {
      selected(option.name);
      if (handleOnChange) {
        handleOnChange(option);
      }
      setIsOpen(false);
    }
  };

  const displayValue = values || placeholder;

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      {lableText ? (
        <TextLable disabled={disabled}>{lableText}</TextLable>
      ) : null}

      <Box sx={{ position: 'relative' }} ref={dropdownRef}>
        <DropdownInput
          onClick={handleToggle}
          disabled={disabled}
          error={error}
          isOpen={isOpen}
          className={disabled ? 'disabled' : ''}
        >
          {displayValue}
        </DropdownInput>

        <ArrowDownCustom
          lableText={!!lableText}
          disabled={disabled}
          isOpen={isOpen}
        />

        {isOpen && (
          <DropdownMenu>
            {list.map((option) => (
              <DropdownOption
                key={option.code}
                onClick={() => handleOptionClick(option)}
              >
                {option.name}
              </DropdownOption>
            ))}
          </DropdownMenu>
        )}
      </Box>

      {message ? <ErrorTypography>{message}</ErrorTypography> : null}
    </Box>
  );
};

BaseSelectBox.propTypes = {
  list: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      code: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ),
  values: PropTypes.string,
  placeholder: PropTypes.string,
  sx: PropTypes.object,
  selected: PropTypes.func,
  message: PropTypes.string,
  error: PropTypes.bool,
  lableText: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  handleOnChange: PropTypes.func,
};

export default BaseSelectBox;
